import { placeOf, type IdeaPlace, type Location, type Spot } from '@shh/shared';
import { funText } from '../i18n/fun';
import { locale } from './locale';

/**
 * Finding a trip destination by its name on OpenStreetMap – free, no key, no account:
 *  - Photon (komoot) for the suggestions while typing, nearest to the family's town first
 *  - Nominatim for the details of the one picked (website, phone, opening hours, entry fee)
 * Only the typed name and the town's rough position leave the device (DSGVO).
 */
export interface SpotHit {
  /** "W484314093" */
  osm: string;
  name: string;
  /** "Hanstedt" / "Wildpark 1, 21271 Hanstedt" */
  town?: string;
  address?: string;
  lat: number;
  lon: number;
  /** "Wildpark", "Museum" … */
  kind?: string;
  place: IdeaPlace;
}

interface PhotonFeature {
  geometry: { coordinates: [number, number] };
  properties: {
    osm_type: string;
    osm_id: number;
    osm_key?: string;
    osm_value?: string;
    name?: string;
    street?: string;
    housenumber?: string;
    postcode?: string;
    city?: string;
    district?: string;
    county?: string;
  };
}

/** Bus stops, car parks and roads share the name of a park – they are never the destination. */
const NOT_A_DESTINATION = new Set([
  'highway',
  'railway',
  'public_transport',
  'boundary',
  'building',
  'aeroway',
]);
const NOT_HERE = new Set([
  'parking',
  'parking_entrance',
  'bus_station',
  'parking_space',
  'toilets',
]);

const KIND: Record<string, string> = funText.kinds;

/**
 * Families go on day trips: first look ~150 km around home (the bias alone still let Mike find
 * places abroad, 03.10.), then all of Germany, and only then anywhere.
 */
const AROUND = { lat: 1.4, lon: 2.2 };
const GERMANY = '5.8,47.2,15.1,55.1';

export async function searchSpots(q: string, near?: Location): Promise<SpotHit[]> {
  if (q.trim().length < 3) return [];
  const boxes = [
    near &&
      [
        near.longitude - AROUND.lon,
        near.latitude - AROUND.lat,
        near.longitude + AROUND.lon,
        near.latitude + AROUND.lat,
      ].join(','),
    GERMANY,
    undefined,
  ];
  for (const bbox of [...new Set(boxes.filter((b, i) => b || i === 2))]) {
    const hits = await photon(q, near, bbox);
    if (hits.length) return hits;
  }
  return [];
}

async function photon(q: string, near?: Location, bbox?: string): Promise<SpotHit[]> {
  const params = new URLSearchParams({ q: q.trim(), limit: '8', lang: 'de' });
  if (near) {
    params.set('lat', String(near.latitude));
    params.set('lon', String(near.longitude));
  }
  if (bbox) params.set('bbox', bbox);
  const res = await fetch(`https://photon.komoot.io/api/?${params}`);
  if (!res.ok) return [];
  const json = (await res.json()) as { features?: PhotonFeature[] };
  const seen = new Set<string>();
  return (json.features ?? [])
    .filter(
      (f) =>
        f.properties.name &&
        !NOT_A_DESTINATION.has(f.properties.osm_key ?? '') &&
        !NOT_HERE.has(f.properties.osm_value ?? ''),
    )
    .map((f) => {
      const p = f.properties;
      const town = p.city ?? p.district ?? p.county;
      const street = [p.street, p.housenumber].filter(Boolean).join(' ');
      const place = [p.postcode, town].filter(Boolean).join(' ');
      return {
        osm: `${p.osm_type}${p.osm_id}`,
        name: p.name!,
        town,
        address: [street, place].filter(Boolean).join(', ') || undefined,
        lat: round(f.geometry.coordinates[1]),
        lon: round(f.geometry.coordinates[0]),
        kind: KIND[p.osm_value ?? ''],
        place: placeOf(p.osm_key, p.osm_value),
      };
    })
    .filter((h) => {
      // the same place twice (area and entrance) – keep the first
      const key = `${h.name}|${h.town ?? ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 5);
}

const round = (n: number) => Math.round(n * 10_000) / 10_000;

/** The details for the place picked; without them the hit alone is still useful. */
export async function spotDetails(hit: SpotHit): Promise<{ spot: Spot; free: boolean }> {
  const spot: Spot = {
    name: hit.name.slice(0, 120),
    address: hit.address?.slice(0, 160),
    lat: hit.lat,
    lon: hit.lon,
    osm: hit.osm,
  };
  let free = false;
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/lookup?${new URLSearchParams({
        osm_ids: hit.osm,
        format: 'jsonv2',
        extratags: '1',
        'accept-language': locale,
      })}`,
    );
    const [found] = res.ok
      ? ((await res.json()) as { extratags?: Record<string, string> | null }[])
      : [];
    const tags = found?.extratags ?? {};
    const website = tags.website ?? tags['contact:website'] ?? tags.url;
    if (website && /^https?:\/\//.test(website) && website.length <= 300) spot.website = website;
    const phone = tags.phone ?? tags['contact:phone'];
    if (phone && phone.length <= 40) spot.phone = phone;
    if (tags.opening_hours && tags.opening_hours.length <= 200) spot.hours = tags.opening_hours;
    free = tags.fee === 'no';
  } catch {
    // offline or the details service is busy – name, address and distance are enough
  }
  // Firestore rejects undefined values
  return {
    spot: Object.fromEntries(Object.entries(spot).filter(([, v]) => v !== undefined)) as Spot,
    free,
  };
}
